/**
 * The nine places this app goes: a rail down the side of the window, a bar across the phone.
 *
 * Arrange, Write and Editor are the three that do something -- two run the rules backwards,
 * one over a piece of music and one over a brief, and the third is a notation editor with no
 * opinion about any of this. Arrange comes first because arranging is what the app is for, and
 * Library, beside it, is where a piece to arrange is found. The three after them are the book, the rules read out of it, and the measurements
 * that say whether the rules are worth anything. Lab and Read are last, and nobody arranging a
 * piece needs to go to either: Lab is Arrange with every choice the app makes there set by
 * hand, and Read is where a machine's reading of a printed page is laid beside the page and
 * counted.
 *
 * The rail is dark because everything around a score in a notation program is: the chrome
 * recedes so that the brightest thing on the screen is the paper. Which board you are on is
 * said twice, by a lit panel and by a bar down its leading edge, because an icon rail read at
 * a glance needs the answer to survive being glanced at.
 */

import { Link, useLocation } from "react-router-dom";

export interface Board {
  readonly to: string;
  readonly label: string;
  /**
   * Deeper routes that belong to this board without hanging off its path. A brief written on
   * Write is read on `/score` and saved on `/export`, and the compass tables are the Rules
   * board's own appendix; a rail that went dark on any of them would be telling the reader
   * they had left the app.
   */
  readonly owns: readonly string[];
  readonly glyph: string;
}

export const BOARDS: readonly Board[] = [
  {
    to: "/arrange",
    label: "Arrange",
    owns: [],
    glyph:
      "M4 15.5a2 2 0 1 0 4 0 2 2 0 1 0-4 0 M8 15.5V4l8-1.5V13 M12 13a2 2 0 1 0 4 0 2 2 0 1 0-4 0",
  },
  {
    to: "/library",
    label: "Library",
    owns: [],
    glyph: "M3 3.5h3v13H3z M7.5 3.5h3v13h-3z M12 4.2l2.9-.8 3.2 12.4-2.9.8z",
  },
  {
    to: "/write",
    label: "Write",
    owns: ["/score", "/export"],
    glyph: "M4 14.5 13 5.5l2.5 2.5-9 9H4z M13.5 5 15 3.5 17 5.5 15.5 7z",
  },
  {
    to: "/book",
    label: "Book",
    owns: [],
    glyph: "M3 4h6a2 2 0 0 1 2 2v11a3 3 0 0 0-2-1H3zM17 4h-4a2 2 0 0 0-2 2v11a3 3 0 0 1 2-1h4z",
  },
  {
    to: "/rules",
    label: "Rules",
    owns: ["/instruments"],
    glyph: "M3 5h14M3 10h14M3 15h9",
  },
  {
    to: "/editor",
    label: "Editor",
    owns: [],
    glyph: "M3 5h14M3 9h14M3 13h14M3 17h14 M7 15v-8l7-1.5v8",
  },
  {
    to: "/evidence",
    label: "Evidence",
    owns: [],
    glyph: "M10 2 3 6v5c0 4 3 6.5 7 7 4-.5 7-3 7-7V6z",
  },
  {
    to: "/lab",
    label: "Lab",
    owns: [],
    glyph: "M8 3h4 M8.5 3v5L4 16a1 1 0 0 0 .9 1.5h10.2A1 1 0 0 0 16 16l-4.5-8V3 M6.3 12.5h7.4",
  },
  {
    to: "/read",
    label: "Read",
    owns: [],
    glyph: "M5 2.5h6.5L15 6v11.5H5z M11.5 2.5V6H15 M7.5 9.5h5 M7.5 12h5 M7.5 14.5h5",
  },
];

/** Whether `pathname` is `at` or something under it, without matching `/books` from `/book`. */
function under(pathname: string, at: string): boolean {
  return pathname === at || pathname.startsWith(`${at}/`);
}

/**
 * The board a path is on, or nothing at all -- which is what a 404 is.
 *
 * The rail and the title bar both ask this, so they cannot disagree about where the reader
 * is. A board's own path wins over another board's claim on a deeper route, so a claim can
 * never quietly capture a real board.
 */
export function boardAt(pathname: string): Board | null {
  const own = BOARDS.find((board) => under(pathname, board.to));
  if (own !== undefined) return own;
  return BOARDS.find((board) => board.owns.some((path) => under(pathname, path))) ?? null;
}

const LINK = [
  "group relative flex min-h-[54px] flex-1 flex-col items-center justify-center gap-1",
  "text-[10px] font-medium tracking-[0.02em]",
  "md:min-h-[58px] md:w-full md:flex-none md:rounded-md",
].join(" ");

export default function TabBar(): JSX.Element {
  const here = boardAt(useLocation().pathname);

  return (
    <nav
      aria-label="Sections"
      className="z-20 flex shrink-0 border-shell-edge bg-shell pb-[env(safe-area-inset-bottom)] md:w-[74px] md:flex-col md:gap-0.5 md:border-r md:p-2 md:pb-2"
    >
      {BOARDS.map((board) => {
        const active = board === here;
        return (
          <Link
            key={board.to}
            to={board.to}
            aria-current={active ? "page" : undefined}
            className={`${LINK} ${
              active
                ? "bg-shell-key text-shell-lit"
                : "text-shell-dim hover:bg-shell-bar hover:text-shell-text"
            }`}
          >
            {/* The lit panel alone reads as a hover on a rail this narrow, so the board you
                are on also carries a bar down the edge the rail is attached by. */}
            <span
              aria-hidden="true"
              className={`absolute rounded-full bg-shell-live transition-opacity ${
                active ? "opacity-100" : "opacity-0"
              } inset-x-3 top-0 h-[2px] md:inset-x-auto md:inset-y-1.5 md:left-0 md:h-auto md:w-[3px]`}
            />
            <svg viewBox="0 0 20 20" className="h-[18px] w-[18px]" aria-hidden="true">
              <path
                d={board.glyph}
                fill="none"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeLinejoin="round"
                strokeLinecap="round"
              />
            </svg>
            {board.label}
          </Link>
        );
      })}
    </nav>
  );
}
